<template>
  <div class="w-full">
    <component
      :is="isHeading ? headingTag : 'div'"
      v-if="!isEditing"
      :class="['editable-text-container', editable ? 'cursor-pointer': '', textClass]"
      @click="handleTextClick"
      ref="textContainer"
    >
      <slot :value="modelValue">
        {{ modelValue }}
      </slot>
    </component>
    <div v-else class="editable-text-input-container">
      <slot
        name="input"
        :model-value="editedText"
        :on-update="updateLocalValue"
        :on-blur="saveText"
        :on-enter="saveText"
        :on-escape="cancelEditing"
        :ref-binding="setInputRef"
      >
        <input
          ref="textInput"
          v-model="editedText"
          :class="['form-input w-full border-none bg-transparent p-0 focus:outline-none focus:ring-0', inputClass]"
          :placeholder="placeholder"
          @blur="saveText"
          @keydown.enter="saveText"
          @keydown.esc="cancelEditing"
        />
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  editable: {
    type: Boolean,
    default: true
  },
  textClass: {
    type: String,
    default: ''
  },
  inputClass: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  headingTag: {
    type: String,
    default: 'div',
    validator: (value) => ['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p'].includes(value)
  }
});

const emit = defineEmits(['update:modelValue']);

const isEditing = ref(false);
const editedText = ref(props.modelValue);
const textInput = ref(null);
const textContainer = ref(null);
const clickPosition = ref(0);

const isHeading = computed(() => props.headingTag !== 'div');

// Update local value when incoming value changes
watch(() => props.modelValue, (newValue) => {
  if (!isEditing.value) {
    editedText.value = newValue;
  }
});

// Click handler with cursor position detection
function handleTextClick(event) {
  if (!props.editable) return;

  // Save current text and its container
  const text = props.modelValue;
  const container = textContainer.value;

  if (!container) {
    return startEditing();
  }

  // Calculate click position relative to container
  const rect = container.getBoundingClientRect();
  const clickX = event.clientX - rect.left;

  // Create temporary element to calculate character widths
  const tempElement = document.createElement('span');
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.whiteSpace = 'pre';

  // Copy text styles
  const styles = window.getComputedStyle(container);
  for (const property of ['font-family', 'font-size', 'font-weight', 'letter-spacing']) {
    tempElement.style[property] = styles[property];
  }

  document.body.appendChild(tempElement);

  // Find character position corresponding to click
  let position = 0;
  let currentWidth = 0;

  for (let i = 0; i < text.length; i++) {
    tempElement.textContent = text.substring(0, i + 1);
    const width = tempElement.getBoundingClientRect().width;

    // If container width exceeds click position,
    // we found the character position
    if (width >= clickX) {
      // If click is closer to previous character
      if (i > 0 && (width - clickX > clickX - currentWidth)) {
        position = i;
      } else {
        position = i + 1;
      }
      break;
    }

    currentWidth = width;
    position = i + 1;
  }

  // Remove temporary element
  document.body.removeChild(tempElement);

  // Save position for cursor placement
  clickPosition.value = position;

  // Call edit start function
  startEditing();
}

function startEditing() {
  if (!props.editable) return;

  editedText.value = props.modelValue;
  isEditing.value = true;

  nextTick(() => {
    if (textInput.value) {
      textInput.value.focus();

      // Set cursor to click position
      if (textInput.value.setSelectionRange) {
        try {
          textInput.value.setSelectionRange(clickPosition.value, clickPosition.value);
        } catch (e) {
          console.error('Failed to set cursor position:', e);
        }
      }
    }
  });
}

function saveText() {
  isEditing.value = false;
  if (editedText.value.trim() !== '') {
    emit('update:modelValue', editedText.value);
  } else {
    editedText.value = props.modelValue;
  }
}

function cancelEditing() {
  isEditing.value = false;
  editedText.value = props.modelValue;
}

function updateLocalValue(value) {
  editedText.value = value;
}

function setInputRef(el) {
  textInput.value = el;
}
</script>
